import { useLocks } from '../../api/customerView.ts';
import { useRole } from '../../api/role.ts';
import { Flame } from 'lucide-react';
import { formatPower, formatTemperature, hasPermission, HeatPumpMode, type LoadpointLive } from '@ste/shared';
import { useLive } from '../../api/live.ts';
import { useCommands } from '../../api/queries.ts';
import {
  Card,
  EmptyState,
  PageHeader,
  SegmentedControl,
  SliderSetting,
  Spinner,
  Stat,
} from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { CommandError } from '../charging/CommandError.tsx';
import { HemsBanner } from '../grid/HemsBanner.tsx';

// The heat pump is an evcc loadpoint with an SG-Ready charger (mapping §4):
// active -> boost, inactive -> normal, dim only by the grid operator (§14a).

export function HeatPumpPage() {
  const live = useLive();
  if (!live) return <Spinner label={t('common.loading')} />;
  const heatpumps = live.loadpoints.filter((lp) => lp.kind === 'heatpump');
  return (
    <div className="space-y-4">
      <PageHeader title={t('heatpump.title')} />
      <HemsBanner live={live} />
      {heatpumps.length === 0 && (
        <Card>
          <EmptyState>{t('heatpump.none')}</EmptyState>
        </Card>
      )}
      {heatpumps.map((lp) => (
        <HeatPumpCard key={lp.id} lp={lp} />
      ))}
    </div>
  );
}

export function sgReadyState(lp: LoadpointLive): 'dim' | 'boost' | 'normal' {
  if (lp.dimmed) return 'dim';
  return lp.enabled ? 'boost' : 'normal';
}

function HeatPumpCard({ lp }: { lp: LoadpointLive }) {
  const role = useRole();
  const locked = useLocks().heatpump;
  const canControl = role !== undefined && hasPermission(role, 'site:control') && !locked;
  const commands = useCommands();
  const state = sgReadyState(lp);
  const modes = HeatPumpMode.options.map((value) => ({
    value,
    label: value === 'pv' ? t('mode.heatpump.pv') : t(`mode.${value}`),
  }));
  const mode = lp.mode === 'minpv' ? 'pv' : lp.mode;
  const error = [commands.setMode, commands.setMinTemperature].find((m) => m.isError)?.error;

  return (
    <Card title={lp.title}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Stat
              label={t('heatpump.temperature')}
              value={lp.temperature !== undefined ? formatTemperature(lp.temperature) : '–'}
            />
            <Stat
              label={t('battery.power')}
              value={formatPower(lp.power)}
              tone={lp.power > 0 ? 'default' : 'muted'}
            />
          </div>
          <div
            className={`flex items-center gap-3 rounded-xl border p-4 ${
              state === 'dim'
                ? 'border-warm/40 bg-warm-soft'
                : state === 'boost'
                  ? 'border-accent/40 bg-accent-soft'
                  : 'border-border bg-surface-2'
            }`}
          >
            <Flame
              className={
                state === 'boost' ? 'text-accent-text' : state === 'dim' ? 'text-warm-text' : 'text-muted'
              }
              aria-hidden
            />
            <div>
              <div className="text-sm text-muted">{t('heatpump.state')}</div>
              <div className="font-semibold">{t(`heatpump.state.${state}`)}</div>
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <div className="mb-2 text-sm text-muted">{t('heatpump.mode')}</div>
            <SegmentedControl
              label={t('heatpump.mode')}
              options={modes}
              value={mode}
              disabled={!canControl || commands.setMode.isPending}
              onChange={(next) => commands.setMode.mutate({ id: lp.id, mode: next })}
            />
            {mode && <p className="mt-2 text-sm text-muted">{t(`mode.hint.heatpump.${mode}`)}</p>}
            {!canControl && (
              <p className="mt-2 text-sm text-muted">
                {locked ? t('locks.installer') : t('common.noPermission')}
              </p>
            )}
          </div>
          <SliderSetting
            label={t('heatpump.minTemperature')}
            value={lp.minTemperature ?? 20}
            min={20}
            max={65}
            format={(v) => formatTemperature(v)}
            disabled={!canControl}
            onCommit={(temperature) => commands.setMinTemperature.mutate({ id: lp.id, temperature })}
          />
          {error && <CommandError error={error} />}
        </div>
      </div>
    </Card>
  );
}
